import {Button} from '@/components/ui/button';
import {Input} from '@/components/ui/input';
import {Label} from '@/components/ui/label';
import {Switch} from '@/components/ui/switch';
import {QuizConfig, QuizPage, QuizOption, LoaderAnimationType} from '@/types/rewards';
import {Plus, Trash2, ChevronUp, ChevronDown, HelpCircle, Loader2, Image, BellRing} from 'lucide-react';
import {Textarea} from '@/components/ui/textarea';

interface QuizPageBuilderProps {
    quizConfig: QuizConfig;
    onChange: (config: QuizConfig) => void;
    disabled: boolean;
}

let pageCounter = 0;
let optionCounter = 0;

function generatePageId(): string {
    pageCounter++;
    return `page_${Date.now()}_${pageCounter}`;
}

function generateOptionId(): string {
    optionCounter++;
    return `opt_${Date.now()}_${optionCounter}`;
}

export const QuizPageBuilder = ({quizConfig, onChange, disabled}: QuizPageBuilderProps) => {
    const pages = quizConfig.pages || [];

    const updatePages = (newPages: QuizPage[]) => {
        // Recalculate order
        const ordered = newPages.map((p, i) => ({...p, order: i + 1}));
        onChange({pages: ordered});
    };

    const addQuestionPage = () => {
        const newPage: QuizPage = {
            id: generatePageId(),
            type: 'question',
            order: pages.length + 1,
            question_text: '',
            options: [
                {id: generateOptionId(), text: '', value: ''},
                {id: generateOptionId(), text: '', value: ''},
            ],
        };
        updatePages([...pages, newPage]);
    };

    const addLoaderPage = () => {
        const newPage: QuizPage = {
            id: generatePageId(),
            type: 'loader',
            order: pages.length + 1,
            loader_text: '',
            duration: 3,
            wait_for_cascade: false,
            loader_animation: 'spinner',
        };
        updatePages([...pages, newPage]);
    };

    const addPushNotificationPage = () => {
        const newPage: QuizPage = {
            id: generatePageId(),
            type: 'push_notification',
            order: pages.length + 1,
            title: '',
            description: '',
            button_text: '',
        };
        updatePages([...pages, newPage]);
    };

    const addImageGridPage = () => {
        const newPage: QuizPage = {
            id: generatePageId(),
            type: 'image_grid',
            order: pages.length + 1,
            subtitle: '',
            question_text: '',
            options: [
                {id: generateOptionId(), text: '', value: '', image_url: ''},
                {id: generateOptionId(), text: '', value: '', image_url: ''},
                {id: generateOptionId(), text: '', value: '', image_url: ''},
                {id: generateOptionId(), text: '', value: '', image_url: ''},
            ],
        };
        updatePages([...pages, newPage]);
    };

    const removePage = (index: number) => {
        const newPages = pages.filter((_, i) => i !== index);
        updatePages(newPages);
    };

    const movePage = (index: number, direction: 'up' | 'down') => {
        const newPages = [...pages];
        const targetIndex = direction === 'up' ? index - 1 : index + 1;
        if (targetIndex < 0 || targetIndex >= newPages.length) return;
        [newPages[index], newPages[targetIndex]] = [newPages[targetIndex], newPages[index]];
        updatePages(newPages);
    };

    const updatePage = (index: number, updates: Partial<QuizPage>) => {
        const newPages = [...pages];
        newPages[index] = {...newPages[index], ...updates};
        updatePages(newPages);
    };

    const addOption = (pageIndex: number) => {
        const page = pages[pageIndex];
        const options = page.options || [];
        const newOptions = [...options, {id: generateOptionId(), text: '', value: ''}];
        updatePage(pageIndex, {options: newOptions});
    };

    const removeOption = (pageIndex: number, optIndex: number) => {
        const page = pages[pageIndex];
        const options = page.options || [];
        if (options.length <= 2) return; // Minimum 2 options
        const newOptions = options.filter((_, i) => i !== optIndex);
        updatePage(pageIndex, {options: newOptions});
    };

    const updateOption = (pageIndex: number, optIndex: number, updates: Partial<QuizOption>) => {
        const page = pages[pageIndex];
        const options = [...(page.options || [])];
        options[optIndex] = {...options[optIndex], ...updates};
        updatePage(pageIndex, {options});
    };

    return (
        <div className="space-y-4">
            <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900">Páginas do Quiz</h3>
                <span className="text-sm text-gray-500">{pages.length} página(s)</span>
            </div>

            {pages.length === 0 && (
                <div className="text-center py-8 text-gray-500 border-2 border-dashed rounded-lg">
                    <p className="mb-2">Nenhuma página configurada</p>
                    <p className="text-sm">Adicione perguntas e loaders ao quiz</p>
                </div>
            )}

            <div className="space-y-3">
                {pages.map((page, index) => (
                    <div
                        key={page.id}
                        className={`border rounded-lg p-4 ${
                            page.type === 'question'
                                ? 'border-blue-200 bg-blue-50/50'
                                : page.type === 'image_grid'
                                    ? 'border-green-200 bg-green-50/50'
                                    : page.type === 'push_notification'
                                        ? 'border-purple-200 bg-purple-50/50'
                                        : 'border-amber-200 bg-amber-50/50'
                        }`}
                    >
                        {/* Page Header */}
                        <div className="flex items-center justify-between mb-3">
                            <div className="flex items-center gap-2">
                                {page.type === 'question' ? (
                                    <HelpCircle className="h-4 w-4 text-blue-600"/>
                                ) : page.type === 'image_grid' ? (
                                    <Image className="h-4 w-4 text-green-600"/>
                                ) : page.type === 'push_notification' ? (
                                    <BellRing className="h-4 w-4 text-purple-600"/>
                                ) : (
                                    <Loader2 className="h-4 w-4 text-amber-600"/>
                                )}
                                <span className="text-sm font-medium">
                                    {page.type === 'question'
                                        ? 'Pergunta'
                                        : page.type === 'image_grid'
                                            ? 'Grid de Imagens'
                                            : page.type === 'push_notification'
                                                ? 'Push Notification'
                                                : 'Loader'} #{index + 1}
                                </span>
                            </div>
                            <div className="flex items-center gap-1">
                                <Button
                                    type="button"
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => movePage(index, 'up')}
                                    disabled={index === 0 || disabled}
                                    className="h-7 w-7 p-0"
                                >
                                    <ChevronUp className="h-4 w-4"/>
                                </Button>
                                <Button
                                    type="button"
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => movePage(index, 'down')}
                                    disabled={index === pages.length - 1 || disabled}
                                    className="h-7 w-7 p-0"
                                >
                                    <ChevronDown className="h-4 w-4"/>
                                </Button>
                                <Button
                                    type="button"
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => removePage(index)}
                                    disabled={disabled}
                                    className="h-7 w-7 p-0 text-red-500 hover:text-red-700"
                                >
                                    <Trash2 className="h-4 w-4"/>
                                </Button>
                            </div>
                        </div>

                        {/* Question Page Content */}
                        {page.type === 'question' && (
                            <div className="space-y-3">
                                <div>
                                    <Label className="text-sm">Pergunta</Label>
                                    <Input
                                        value={page.question_text || ''}
                                        onChange={(e) => updatePage(index, {question_text: e.target.value})}
                                        placeholder="Ex: Qual seu interesse principal?"
                                        disabled={disabled}
                                    />
                                </div>
                                <div>
                                    <Label className="text-sm">Opções de resposta</Label>
                                    <div className="space-y-2 mt-1">
                                        {(page.options || []).map((opt, optIndex) => (
                                            <div key={opt.id} className="flex gap-2">
                                                <Input
                                                    value={opt.text}
                                                    onChange={(e) => updateOption(index, optIndex, {text: e.target.value})}
                                                    placeholder={`Opção ${optIndex + 1}`}
                                                    disabled={disabled}
                                                    className="flex-1"
                                                />
                                                <Input
                                                    value={opt.value || ''}
                                                    onChange={(e) => updateOption(index, optIndex, {value: e.target.value})}
                                                    placeholder="Valor (opcional)"
                                                    disabled={disabled}
                                                    className="w-32"
                                                />
                                                <Button
                                                    type="button"
                                                    variant="ghost"
                                                    size="sm"
                                                    onClick={() => removeOption(index, optIndex)}
                                                    disabled={(page.options || []).length <= 2 || disabled}
                                                    className="h-9 w-9 p-0 text-red-400 hover:text-red-600"
                                                >
                                                    <Trash2 className="h-3 w-3"/>
                                                </Button>
                                            </div>
                                        ))}
                                    </div>
                                    <Button
                                        type="button"
                                        variant="outline"
                                        size="sm"
                                        onClick={() => addOption(index)}
                                        disabled={disabled}
                                        className="mt-2"
                                    >
                                        <Plus className="h-3 w-3 mr-1"/>
                                        Adicionar opção
                                    </Button>
                                </div>
                            </div>
                        )}

                        {/* Loader Page Content */}
                        {page.type === 'loader' && (
                            <div className="space-y-3">
                                <div>
                                    <Label className="text-sm">Texto do loader</Label>
                                    <Input
                                        value={page.loader_text || ''}
                                        onChange={(e) => updatePage(index, {loader_text: e.target.value})}
                                        placeholder="Ex: Processando ofertas..."
                                        disabled={disabled}
                                    />
                                </div>
                                <div>
                                    <Label className="text-sm">Animação</Label>
                                    <div className="flex gap-2 mt-1">
                                        {([
                                            {value: 'spinner' as LoaderAnimationType, label: 'Spinner', icon: '⟳'},
                                            {value: 'dots' as LoaderAnimationType, label: 'Dots', icon: '•••'},
                                            {value: 'pulse' as LoaderAnimationType, label: 'Pulse', icon: '◉'},
                                            {value: 'bars' as LoaderAnimationType, label: 'Bars', icon: '▮▮▮'},
                                        ]).map((anim) => (
                                            <button
                                                key={anim.value}
                                                type="button"
                                                onClick={() => updatePage(index, {loader_animation: anim.value})}
                                                disabled={disabled}
                                                className={`flex-1 px-2 py-2 rounded-lg border text-xs font-medium transition-colors ${
                                                    (page.loader_animation || 'spinner') === anim.value
                                                        ? 'border-amber-500 bg-amber-50 text-amber-700'
                                                        : 'border-gray-200 text-gray-500 hover:bg-gray-50'
                                                }`}
                                            >
                                                <span className="block text-base mb-0.5">{anim.icon}</span>
                                                {anim.label}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                                <div className="flex gap-4">
                                    <div className="flex-1">
                                        <Label className="text-sm">Duração (segundos)</Label>
                                        <Input
                                            type="number"
                                            min={1}
                                            max={60}
                                            value={page.duration || 3}
                                            onChange={(e) => updatePage(index, {duration: parseInt(e.target.value) || 3})}
                                            disabled={disabled}
                                        />
                                    </div>
                                    <div className="flex items-center gap-2 pt-5">
                                        <Switch
                                            checked={page.wait_for_cascade || false}
                                            onCheckedChange={(checked) => updatePage(index, {wait_for_cascade: checked})}
                                            disabled={disabled}
                                        />
                                        <Label className="text-sm text-gray-600">Aguardar cascade</Label>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Push Notification Page Content */}
                        {page.type === 'push_notification' && (
                            <div className="space-y-3">
                                <div>
                                    <Label className="text-sm">Título</Label>
                                    <Input
                                        value={page.title || ''}
                                        onChange={(e) => updatePage(index, {title: e.target.value})}
                                        placeholder="Ex: Ative as notificações"
                                        disabled={disabled}
                                    />
                                </div>
                                <div>
                                    <Label className="text-sm">Descrição</Label>
                                    <Textarea
                                        value={page.description || ''}
                                        onChange={(e) => updatePage(index, {description: e.target.value})}
                                        placeholder="Ex: Receba as melhores ofertas em primeira mão"
                                        disabled={disabled}
                                        rows={2}
                                    />
                                </div>
                                <div>
                                    <Label className="text-sm">Texto do botão</Label>
                                    <Input
                                        value={page.button_text || ''}
                                        onChange={(e) => updatePage(index, {button_text: e.target.value})}
                                        placeholder="Ex: Permitir notificações"
                                        disabled={disabled}
                                    />
                                </div>
                                <p className="text-xs text-purple-700/70">
                                    Ao clicar, o navegador pedirá permissão de push. O quiz avança automaticamente independente da resposta.
                                </p>
                            </div>
                        )}

                        {/* Image Grid Page Content */}
                        {page.type === 'image_grid' && (
                            <div className="space-y-3">
                                <div>
                                    <Label className="text-sm">Subtítulo (opcional)</Label>
                                    <Input
                                        value={page.subtitle || ''}
                                        onChange={(e) => updatePage(index, {subtitle: e.target.value})}
                                        placeholder="Ex: It takes less than 15 seconds"
                                        disabled={disabled}
                                    />
                                </div>
                                <div>
                                    <Label className="text-sm">Pergunta</Label>
                                    <Input
                                        value={page.question_text || ''}
                                        onChange={(e) => updatePage(index, {question_text: e.target.value})}
                                        placeholder="Ex: Qual seu estilo?"
                                        disabled={disabled}
                                    />
                                </div>
                                <div>
                                    <Label className="text-sm">Opções com imagem</Label>
                                    <div className="grid grid-cols-2 gap-2 mt-1">
                                        {(page.options || []).map((opt, optIndex) => (
                                            <div key={opt.id} className="border rounded-lg p-2 space-y-1.5 bg-white">
                                                <Input
                                                    value={opt.image_url || ''}
                                                    onChange={(e) => updateOption(index, optIndex, {image_url: e.target.value})}
                                                    placeholder="URL da imagem"
                                                    disabled={disabled}
                                                    className="text-xs"
                                                />
                                                {opt.image_url && (
                                                    <img
                                                        src={opt.image_url}
                                                        alt={opt.text}
                                                        className="w-full h-16 object-cover rounded"
                                                    />
                                                )}
                                                <Input
                                                    value={opt.text}
                                                    onChange={(e) => updateOption(index, optIndex, {text: e.target.value})}
                                                    placeholder="Label"
                                                    disabled={disabled}
                                                    className="text-xs"
                                                />
                                                <div className="flex gap-1">
                                                    <Input
                                                        value={opt.value || ''}
                                                        onChange={(e) => updateOption(index, optIndex, {value: e.target.value})}
                                                        placeholder="Valor"
                                                        disabled={disabled}
                                                        className="text-xs flex-1"
                                                    />
                                                    <Button
                                                        type="button"
                                                        variant="ghost"
                                                        size="sm"
                                                        onClick={() => removeOption(index, optIndex)}
                                                        disabled={(page.options || []).length <= 2 || disabled}
                                                        className="h-8 w-8 p-0 text-red-400"
                                                    >
                                                        <Trash2 className="h-3 w-3"/>
                                                    </Button>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                    <Button
                                        type="button"
                                        variant="outline"
                                        size="sm"
                                        onClick={() => addOption(index)}
                                        disabled={disabled}
                                        className="mt-2"
                                    >
                                        <Plus className="h-3 w-3 mr-1"/>
                                        Adicionar opção
                                    </Button>
                                </div>
                            </div>
                        )}
                    </div>
                ))}
            </div>

            {/* Add Page Buttons */}
            <div className="flex gap-2 pt-2 flex-wrap">
                <Button
                    type="button"
                    variant="outline"
                    onClick={addQuestionPage}
                    disabled={disabled}
                    className="flex-1 border-blue-200 text-blue-700 hover:bg-blue-50"
                >
                    <HelpCircle className="h-4 w-4 mr-2"/>
                    Pergunta
                </Button>
                <Button
                    type="button"
                    variant="outline"
                    onClick={addImageGridPage}
                    disabled={disabled}
                    className="flex-1 border-green-200 text-green-700 hover:bg-green-50"
                >
                    <Image className="h-4 w-4 mr-2"/>
                    Grid Imagens
                </Button>
                <Button
                    type="button"
                    variant="outline"
                    onClick={addLoaderPage}
                    disabled={disabled}
                    className="flex-1 border-amber-200 text-amber-700 hover:bg-amber-50"
                >
                    <Loader2 className="h-4 w-4 mr-2"/>
                    Adicionar Loader
                </Button>
                <Button
                    type="button"
                    variant="outline"
                    onClick={addPushNotificationPage}
                    disabled={disabled}
                    className="flex-1 border-purple-200 text-purple-700 hover:bg-purple-50"
                >
                    <BellRing className="h-4 w-4 mr-2"/>
                    Push Notification
                </Button>
            </div>
        </div>
    );
};
